import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ExpandableRow,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import useSensitiveDataFlag from '@utils/useSensitiveDataFlag'

import { IBNetworkPlans } from '@model/MultiTierDto'

import styles from './MultiTierNetworkPage.module.scss'

interface Props {
  sticky?: 'left'
  searchValue: string | null
  tableQuery: PaginatedQueryResult<IBNetworkPlans>
}

export const MultiTierNetworkTable: FC<Props> = (props) => {
  const { t } = useTranslation()
  const { tableQuery, sticky, searchValue } = props
  const { data, isLoading } = tableQuery
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const sensitiveDataFlag = useSensitiveDataFlag()

  const toggleRow = (id: string) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))

  return (
    <Table
      isLoading={isLoading}
      noResults={
        !data.length && (
          <TableNoResults
            title={searchValue ? t('No results found') : t('IB.MultiTier.No Network to display')}
          />
        )
      }
      sticky={sticky}
    >
      <TableHeader>
        <TableRow>
          <TableHead>{t('IB.MultiTier.Accounts')}</TableHead>
          <TableHead align='right'>{t('IB.MultiTier.Level')}</TableHead>
          <TableHead align='right'>{t('IB.MultiTier.Clients')}</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {data.map((plan) => {
          const id = plan.introducingBroker.id
          return (
            <ExpandableRow
              className={styles.tableCell}
              key={id}
              row={{
                ...plan,
                id: plan.introducingBroker.id,
                subRows: plan.subAccounts?.map((subAccount) => ({
                  ...subAccount,
                  id: subAccount.introducingBroker.id,
                })),
              }}
              isExpanded={expandedRows[id]}
              onToggle={() => toggleRow(id)}
              renderParent={(r) => [
                { value: r.introducingBroker.name },
                { value: r.level, align: 'right', width: 476 },
                { value: r.numberOfClients, align: 'right', width: 476 },
              ]}
              renderSubRow={(sub) => [
                { value: sub.introducingBroker.name },
                { value: sub.level, align: 'right', width: 476 },
                { value: sub.numberOfClients, align: 'right', width: 476 },
              ]}
              isSensitiveData={sensitiveDataFlag}
            />
          )
        })}
      </TableBody>
    </Table>
  )
}
